/**
 * O dono escolhe o que a equipe dele vê (C2).
 *
 * Terceira camada da cascata: o SE_WEB decide primeiro, o dono restringe dentro
 * do que sobrou. Quando o SE_WEB já escondeu uma tela, **a linha aparece sem
 * interruptor** — um controle que não muda nada é pior que a ausência dele,
 * porque o dono clica, salva e acha que configurou.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2, Lock } from "lucide-react";
import React, { useEffect, useState } from "react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import portalApi from "@/services/portalApi";

type NivelSeweb = "todos" | "so_dono" | "ninguem";

interface TelaDaEquipe {
  slug: string;
  nome: string;
  escondida: boolean;
  nivel_seweb: NivelSeweb;
  editavel: boolean;
}

/**
 * Por que a linha não pode ser mexida — o dono merece saber o motivo.
 *
 * M9 (revisão final) — "todos" não aparece mais hoje: `editavel` no backend
 * é `seweb is TODOS and slug in SLUGS_BLOQUEAVEIS`, e nenhuma tela do
 * catálogo é `essencial` desde o PR 5 (nem o Dashboard — ver
 * `TelaPortal.essencial` em `app/core/portal_telas.py`), então
 * `SLUGS_BLOQUEAVEIS == SLUGS` e `nivel_seweb === "todos"` implica
 * `editavel`. O campo `essencial` continua existindo no backend pra próxima
 * tela que precisar ser irremovível — daí este motivo ficar aqui, e não
 * mencionar mais "essencial" (o conceito que o PR 5 tirou do Dashboard).
 */
const MOTIVO_BLOQUEIO: Record<NivelSeweb, string> = {
  todos: "A SE Promotora mantém esta tela sempre disponível para a equipe",
  so_dono: "A SE Promotora já reservou esta tela apenas para você",
  ninguem: "A SE Promotora desativou esta tela",
};

const CHAVE = ["portal", "telas-equipe"];

const PortalTelasDaEquipe: React.FC = () => {
  const { toast } = useToast();
  const qc = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<{ telas: TelaDaEquipe[] }>({
    queryKey: CHAVE,
    queryFn: async () => (await portalApi.get("/portal/telas-equipe")).data,
  });

  const [escondidas, setEscondidas] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (data) {
      setEscondidas(new Set(data.telas.filter((t) => t.escondida).map((t) => t.slug)));
    }
  }, [data]);

  const salvar = useMutation({
    mutationFn: async (slugs: string[]) =>
      (await portalApi.put("/portal/telas-equipe", { escondidas: slugs })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAVE });
      toast({
        title: "Telas atualizadas",
        description:
          "Vale a partir de agora. O menu dos seus funcionários atualiza em até " +
          "15 minutos, quando a sessão deles renova.",
      });
    },
    onError: () =>
      toast({
        title: "Não foi possível salvar",
        description: "Tente de novo. Se persistir, fale com o suporte.",
        variant: "destructive",
      }),
  });

  const original = new Set((data?.telas ?? []).filter((t) => t.escondida).map((t) => t.slug));
  const mudou = escondidas.size !== original.size || [...escondidas].some((s) => !original.has(s));

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Carregando telas…
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription className="flex items-center justify-between gap-4">
          Não foi possível carregar as telas.
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Tentar novamente
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Telas da Equipe</h1>
        <p className="text-sm text-muted-foreground">
          Escolha o que os seus funcionários enxergam no portal. O que você desligar aqui some do
          menu deles — você continua com acesso normal.
        </p>
      </div>

      <ul className="divide-y rounded-lg border">
        {(data?.telas ?? []).map((tela) => {
          const desligada = escondidas.has(tela.slug);
          return (
            <li key={tela.slug} className="flex flex-wrap items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="font-medium">{tela.nome}</p>
                <p className="text-xs text-muted-foreground">
                  {!tela.editavel
                    ? MOTIVO_BLOQUEIO[tela.nivel_seweb]
                    : desligada
                      ? "Os seus funcionários não veem esta tela"
                      : "Os seus funcionários veem esta tela"}
                </p>
              </div>

              {tela.editavel ? (
                <Switch
                  checked={!desligada}
                  disabled={salvar.isPending}
                  aria-label={`Tela ${tela.nome} para a equipe`}
                  onCheckedChange={() =>
                    setEscondidas((atual) => {
                      const novo = new Set(atual);
                      if (novo.has(tela.slug)) novo.delete(tela.slug);
                      else novo.add(tela.slug);
                      return novo;
                    })
                  }
                />
              ) : (
                /* Sem interruptor de propósito: mexer aqui não mudaria nada.
                   Ver o docstring do arquivo sobre controle inerte. */
                <span
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                  data-testid={`bloqueada-${tela.slug}`}
                >
                  <Lock className="h-3 w-3" />
                  Definido pela SE Promotora
                </span>
              )}
            </li>
          );
        })}
      </ul>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => salvar.mutate([...escondidas])}
          disabled={!mudou || salvar.isPending}
        >
          {salvar.isPending ? "Salvando…" : "Salvar alterações"}
        </Button>
        {mudou && <span className="text-sm text-muted-foreground">Há alterações não salvas.</span>}
      </div>
    </div>
  );
};

export default PortalTelasDaEquipe;
